/**
 * Диагностика канонизации. 02-contracts.md §5 с поправками §Хребет данных 3.4:
 * добавлены entity_order_tie (fatal) и slot_local_ambiguous (warning),
 * удалён entities_merged (слияние теперь часть resplit и отдельным предупреждением не отмечается).
 *
 * Имя предупреждения о голой ссылке — bare_url_in_text (§Ключ, преамбула). Формы
 * bare_url_present из 02-contracts.md §5 не существует: два имени у одного события запрещены.
 */

export type Severity = 'fatal' | 'warning';

export interface Issue {
  readonly code: IssueCode;
  readonly severity: Severity;
  /** По-русски: показывается человеку в UI рядом с блоком «Что изменит Telegram». */
  readonly message: string;
  /** Например 'entities[3].offset'. */
  readonly path?: string;
  readonly detail?: Readonly<Record<string, string | number>>;
}

export const FATAL_ISSUE_CODES = [
  'text_not_well_formed',
  'text_empty_after_canonicalization',
  'text_too_long',
  'text_too_long_bytes',
  /**
   * ЗАРЕЗЕРВИРОВАН, НЕ ВЫДАЁТСЯ. Подписи к картинке больше не существует: пост с картинкой
   * уходит rich-сообщением, и потолок у него общий. Код остаётся в списке по той же причине,
   * по какой `sendPhoto` остаётся кодом 2 в METHOD_CODE, — `ISSUE_CODES` разбирает СТАРЫЕ
   * сохранённые отказы (`api/ui.ts`, `z.enum(ISSUE_CODES)`), и вычеркнутый код сделал бы
   * нечитаемым черновик, отказанный до перехода.
   */
  'caption_too_long',
  /**
   * ЗАРЕЗЕРВИРОВАН, НЕ ВЫДАЁТСЯ с 19.09.2026. Ловил «rich объявлен без причины» — короткий пост
   * без картинки с методом `sendRichMessage`. С политикой бренда «все посты — расширенным
   * сообщением» (docs/release-2026-09-19.md §9.3) это ЗАКОННАЯ форма: метод выбирается при
   * заморозке (`freezeAuthored(…, {richAlways})`) и дальше читается из подписанного, а не
   * выводится заново, — публикатор и сверка идут по `method` контента. Код остаётся в списке
   * по той же причине, что `caption_too_long`: старые сохранённые отказы обязаны разбираться.
   */
  'method_not_derived',
  /**
   * Цитата или блок кода в rich-сообщении не занимает целые строки, вложен не туда или
   * пересекается с другим блоком (`richBlockIssues`, `telegram/rich.ts`). В rich-сообщении они
   * уходят БЛОКАМИ (Bot API 10.3: узлов `pre`/`blockquote` в `RichText` нет), и такую форму
   * Telegram отбил бы 400 уже после одобрения. Отказ здесь дешевле.
   */
  'rich_block_misaligned',
  /** Дескриптор документа (PDF внизу поста, §9.2) не прошёл схему или метод не rich. */
  'document_invalid',
  'entity_out_of_range',
  'entity_zero_length',
  'entity_boundary_in_surrogate_pair',
  'entity_type_not_sendable',
  'entity_type_forbidden_mvp',
  'entity_inside_code_or_pre',
  'entity_crosses_blockquote',
  'entity_order_tie',
  'blockquote_overlap',
  'continuous_entity_overlap',
  'text_link_url_invalid',
  'pre_language_invalid',
  'link_preview_options_conflict',
  'media_invalid',
  'canon_algo_mismatch',
  'unrecognized_field',
] as const;

export const WARNING_ISSUE_CODES = [
  'text_modified_by_canonicalization',
  'control_chars_replaced',
  'cr_removed',
  'trimmed_leading',
  'trimmed_trailing',
  'entity_dropped_by_trim',
  'non_bmp_present',
  'bare_url_in_text',
  'entity_count_high',
  'slot_local_ambiguous',
  /**
   * Карточка превью снята, потому что пост уехал rich-сообщением. Предупреждение, а не
   * отказ: автор указал карточку, транспорт её не поддерживает, и правильное поведение —
   * опубликовать пост без карточки, а не отбить его.
   *
   * Но и молчать нельзя. Человек одобряет ФОРМУ, а карточка — часть формы; снятая молча,
   * она вернулась бы к нему уже из канала. Предупреждения ложатся в `canonicalization_diff`
   * и показываются на экране одобрения блоком «Что изменит Telegram» — то есть ровно там,
   * где человек и ждёт список «что мы сделали с твоим текстом».
   */
  'link_preview_dropped_for_rich',
] as const;

export const ISSUE_CODES = [...FATAL_ISSUE_CODES, ...WARNING_ISSUE_CODES] as const;

export type FatalIssueCode = (typeof FATAL_ISSUE_CODES)[number];
export type WarningIssueCode = (typeof WARNING_ISSUE_CODES)[number];
export type IssueCode = (typeof ISSUE_CODES)[number];

const FATAL_SET: ReadonlySet<string> = new Set(FATAL_ISSUE_CODES);

/** Severity выводится из принадлежности к списку, а не задаётся вызывающим по памяти. */
export function severityOf(code: IssueCode): Severity {
  return FATAL_SET.has(code) ? 'fatal' : 'warning';
}

export function issue(
  code: IssueCode,
  message: string,
  extra?: { path?: string; detail?: Readonly<Record<string, string | number>> },
): Issue {
  const base = { code, severity: severityOf(code), message } as const;
  if (extra?.path !== undefined && extra.detail !== undefined) {
    return { ...base, path: extra.path, detail: extra.detail };
  }
  if (extra?.path !== undefined) return { ...base, path: extra.path };
  if (extra?.detail !== undefined) return { ...base, detail: extra.detail };
  return base;
}

export function hasFatal(issues: readonly Issue[]): boolean {
  for (const i of issues) {
    if (i.severity === 'fatal') return true;
  }
  return false;
}

/** Для лога и алерта: одна строка, стабильный порядок. */
export function formatIssues(issues: readonly Issue[]): string {
  return issues
    .map((i) => {
      const where = i.path === undefined ? '' : ` at ${i.path}`;
      const detail =
        i.detail === undefined
          ? ''
          : ` {${Object.entries(i.detail)
              .map(([k, v]) => `${k}=${String(v)}`)
              .join(', ')}}`;
      return `${i.severity}:${i.code}${where} ${i.message}${detail}`;
    })
    .join('; ');
}

/**
 * Ошибка канонизации и кодирования. Живёт здесь, а не в canonical/canonicalize.ts,
 * чтобы её мог бросать и encode.ts, и entity.ts без циклического импорта;
 * наружу реэкспортируется из canonical/canonicalize.ts, как объявлено в §Хребет данных 6.
 */
export class CanonicalizeError extends Error {
  override readonly name = 'CanonicalizeError';
  readonly code: IssueCode;
  readonly issues: readonly Issue[];

  constructor(code: IssueCode, message: string, issues: readonly Issue[] = []) {
    super(message);
    this.code = code;
    this.issues = issues;
  }
}
